import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslatePipe } from '@ngx-translate/core';
import { NgFor, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { Subject, takeUntil } from 'rxjs';
import {
  TableConfiguration,
  TableConfigurationService,
  TableConfigurationSource,
} from '../../services/table-configuration.service';
import { AuthService } from '../../services/auth.service';
import { User, UserRole } from '../../models/user';

export interface ColumnOption {
  key: string;
  label: string;
  selected: boolean;
  isCustom: boolean;
}

interface ColumnConfigurationDialogData {
  tableId: string;
  baseColumns: ColumnOption[];
  customFieldOptions: ColumnOption[];
}

@Component({
  selector: 'app-column-configuration',
  templateUrl: './column-configuration.component.html',
  styleUrls: ['./column-configuration.component.scss'],
  imports: [TranslatePipe, NgFor, NgIf, FormsModule, DragDropModule],
})
export class ColumnConfigurationComponent implements OnInit, OnDestroy {
  tableId = '';
  baseColumns: ColumnOption[] = [];
  customFieldOptions: ColumnOption[] = [];
  saving = false;
  resetting = false;
  savingOrgDefault = false;
  deletingOrgDefault = false;

  currentSource: TableConfigurationSource | null = null;
  hasOrgDefault = false;

  private tableConfigService = inject(TableConfigurationService);
  private authService = inject(AuthService);
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly dialogConfig = inject(
    DynamicDialogConfig<ColumnConfigurationDialogData>,
  );

  private user: User | null = null;
  private destroy$ = new Subject<void>();

  constructor() {
    Object.assign(this, this.dialogConfig.data);
  }

  get isOrgAdmin(): boolean {
    return (
      this.user?.role === UserRole.ADMIN || this.user?.role === UserRole.MANAGER
    );
  }

  ngOnInit(): void {
    this.authService
      .getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.user = user;
      });
    this.loadConfiguration();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadConfiguration(): void {
    this.tableConfigService
      .getConfiguration(this.tableId)
      .subscribe((config) => {
        if (config) {
          this.applyConfiguration(config);
        }
      });
  }

  private applyConfiguration(config: TableConfiguration): void {
    const selectedKeys = new Set(config.visibleColumns);
    this.currentSource = config.source;

    // Track if org default exists (if we got an ORG_DEFAULT, there's an org default)
    if (config.source === 'ORG_DEFAULT') {
      this.hasOrgDefault = true;
    }

    // Update base columns selection
    this.baseColumns.forEach((col) => {
      col.selected = selectedKeys.has(col.key);
    });

    // Update custom field selection
    this.customFieldOptions.forEach((col) => {
      col.selected = selectedKeys.has(col.key);
    });

    // Reorder based on saved configuration
    const keyToIndex = new Map(
      config.visibleColumns.map((key, idx) => [key, idx]),
    );

    const sortByConfigOrder = (a: ColumnOption, b: ColumnOption): number => {
      const aIdx = keyToIndex.get(a.key) ?? Number.MAX_VALUE;
      const bIdx = keyToIndex.get(b.key) ?? Number.MAX_VALUE;
      return aIdx - bIdx;
    };

    this.baseColumns.sort(sortByConfigOrder);
    this.customFieldOptions.sort(sortByConfigOrder);
  }

  get allOptions(): ColumnOption[] {
    return [...this.baseColumns, ...this.customFieldOptions];
  }

  get selectedOptions(): ColumnOption[] {
    return this.allOptions.filter((opt) => opt.selected);
  }

  toggleColumn(option: ColumnOption): void {
    option.selected = !option.selected;
  }

  dropColumn(event: CdkDragDrop<ColumnOption[]>): void {
    const selectedList = this.selectedOptions;
    moveItemInArray(selectedList, event.previousIndex, event.currentIndex);

    // Update the order in the original arrays
    const newOrder = selectedList.map((opt) => opt.key);
    const keyToNewIndex = new Map(newOrder.map((key, idx) => [key, idx]));

    this.baseColumns.sort((a, b) => {
      if (!a.selected && !b.selected) return 0;
      if (!a.selected) return 1;
      if (!b.selected) return -1;
      return (keyToNewIndex.get(a.key) ?? 0) - (keyToNewIndex.get(b.key) ?? 0);
    });

    this.customFieldOptions.sort((a, b) => {
      if (!a.selected && !b.selected) return 0;
      if (!a.selected) return 1;
      if (!b.selected) return -1;
      return (keyToNewIndex.get(a.key) ?? 0) - (keyToNewIndex.get(b.key) ?? 0);
    });
  }

  dismiss(): void {
    this.dialogRef.close();
  }

  save(): void {
    if (this.saving) {
      return;
    }

    this.saving = true;
    const visibleColumns = this.selectedOptions.map((opt) => opt.key);
    this.dialogRef.close(visibleColumns);

    this.tableConfigService
      .saveConfiguration(this.tableId, visibleColumns)
      .subscribe({
        next: (config) => {
          this.currentSource = config.source;
          this.saving = false;
        },
        error: () => {
          this.saving = false;
          console.error('Failed to save table configuration');
        },
      });
  }

  resetToDefault(): void {
    this.resetting = true;
    this.tableConfigService.deleteConfiguration(this.tableId).subscribe({
      next: () => {
        this.resetting = false;
        this.tableConfigService.clearCache(this.tableId);
        // Reload to get org default or no config
        this.loadConfiguration();
      },
      error: () => {
        this.resetting = false;
      },
    });
  }

  saveAsOrgDefault(): void {
    if (this.savingOrgDefault) {
      return;
    }

    this.savingOrgDefault = true;
    const visibleColumns = this.selectedOptions.map((opt) => opt.key);
    this.dialogRef.close(visibleColumns);

    this.tableConfigService
      .saveOrganizationDefault(this.tableId, visibleColumns)
      .subscribe({
        next: (config) => {
          this.currentSource = config.source;
          this.hasOrgDefault = true;
          this.savingOrgDefault = false;
        },
        error: () => {
          this.savingOrgDefault = false;
          console.error(
            'Failed to save organization default table configuration',
          );
        },
      });
  }

  deleteOrgDefault(): void {
    this.deletingOrgDefault = true;
    this.tableConfigService.deleteOrganizationDefault(this.tableId).subscribe({
      next: () => {
        this.hasOrgDefault = false;
        this.deletingOrgDefault = false;
        this.tableConfigService.clearCache(this.tableId);
        // Reload configuration
        this.loadConfiguration();
      },
      error: () => {
        this.deletingOrgDefault = false;
      },
    });
  }
}
